// src/components/CodeExecutorApp.js
import React, { useState, useCallback, useEffect, useContext, useRef, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Loader2, CheckCircle, XCircle, Code, Terminal, ListChecks, Plus, LogOut, User, Settings, Menu, X, Puzzle, BookOpen, ChevronDown, BarChart2, ChevronUp, LayoutDashboard, Edit, RotateCcw, Play, Users } from 'lucide-react';

import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-java';
import 'prismjs/themes/prism-dark.css';

import { AuthContext } from '../App';
import ProblemManager from './ProblemManager'; // Import the new ProblemManager component
import SectionManager from './SectionManager'; // Import SectionManager component
import ProgressManager from './ProgressManager'; // Import the new ProgressManager component
import ProgressDashboard from './ProgressDashboard'; // Import the new ProgressDashboard component
import StringMethodsReference from './StringMethodsReference'; // Import String Methods Reference
import ProblemHeaderActions from './ProblemHeaderActions'; // Import Problem Header Actions
import { toast } from 'sonner'; // Import toast for notifications
import ReactMarkdown from 'react-markdown'; // Import markdown renderer

import {
    auth,
    db,
    signOut,
    doc,
    getDoc, // <--- Used to fetch user role and daily problem
    setDoc,
    collection,
    query,
    where,
    getDocs,
    orderBy,
    Timestamp,
    onSnapshot
} from '../firebase/firebase';
import { cn } from '../lib/utils'; // Keeping cn utility

const CodeExecutorApp = () => {
    const currentUser = useContext(AuthContext);
    const userId = currentUser ? currentUser.uid : 'anonymous';
    const [isTeacher, setIsTeacher] = useState(false);

    // State to hold all problems fetched from Firestore
    const [allProblems, setAllProblems] = useState([]);
    // State to hold all sections fetched from Firestore (for teacher to manage)
    const [allSections, setAllSections] = useState([]);
    // State for category display order
    const [categoryDisplayOrders, setCategoryDisplayOrders] = useState([]);

    const [currentProblemIndex, setCurrentProblemIndex] = useState(-1); // Index into allProblems, -1 means not initialized
    const [code, setCode] = useState('');
    const [methodName, setMethodName] = useState(''); // CORRECTED: Added useState('')
    const [output, setOutput] = useState(''); // For compilation errors, console output, general messages
    const [detailedTestResults, setDetailedTestResults] = useState([]); // For structured test results table
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null); // ADDED: Definition for error state
    // Modified studentSolutions to store more detailed progress: { problemId: { completed: boolean, passedTests: number, totalTests: number, savedCode: string } }
    const [studentSolutions, setStudentSolutions] = useState({});

    // State for shared daily problem managed by teacher
    const [dailyProblemId, setDailyProblemId] = useState(''); // Stores the ID from Firestore
    // Note: dailyProblemIndex removed - we now use order field for access control




    // Helper function to check if a problem is the daily problem and return star icon
    const getDailyProblemIndicator = useCallback((problemId) => {
        if (problemId === dailyProblemId) {
            return <span className="text-yellow-400 ml-1" title="Daily Problem">⭐</span>;
        }
        return null;
    }, [dailyProblemId]);

    const [settingProblemLoading, setSettingProblemLoading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // NEW: State to manage which view is active
    const [activeView, setActiveView] = useState('solve'); // 'solve', 'edit', 'progress', 'manage', 'sections'

    // NEW: State for Teacher Controls menu visibility
    const [isTeacherMenuOpen, setIsTeacherMenuOpen] = useState(false);
    const teacherMenuRef = useRef(null);

    // State for String Methods Reference modal
    const [isStringMethodsOpen, setIsStringMethodsOpen] = useState(false);
    const codeEditorRef = useRef(null); // Ref for the code editor component

    // NEW: State to manage expanded/collapsed categories in the sidebar
    const [expandedCategories, setExpandedCategories] = useState({});

    // State to track which problem to edit in ProblemManager
    const [problemToEdit, setProblemToEdit] = useState(null);

    // Get the backend API URL from environment variables
    // This will be http://localhost:8080 in development and your Cloud Run URL in production
    const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';


    // Close teacher menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (teacherMenuRef.current && !teacherMenuRef.current.contains(event.target)) {
                setIsTeacherMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Function to update and persist individual student progress
    // Now takes passedTests and totalTests
    const updateProgress = useCallback(async (problemId, completed, passedTests, totalTests, currentCode) => { // Added currentCode
        setStudentSolutions(prevSolutions => {
            const newSolutions = {
                ...prevSolutions,
                [problemId]: {
                    completed: completed,
                    passedTests: passedTests,
                    totalTests: totalTests,
                    savedCode: currentCode // Store the current code in local state
                }
            };
            return newSolutions;
        });

        if (currentUser && problemId) { // Ensure problemId is valid
            try {
                const problemDocRef = doc(db, `users/${userId}/problems_solved`, problemId);
                const problem = allProblems.find(p => p.id === problemId);



                await setDoc(problemDocRef, {
                    completed: completed,
                    timestamp: Timestamp.now(),
                    problemTitle: problem?.title || problemId, // Use title from fetched problems
                    passedTests: passedTests, // Store passed tests count
                    totalTests: totalTests, // Store total tests count
                    savedCode: currentCode // PERSIST THE CURRENT CODE TO FIRESTORE
                }, { merge: true });

            } catch (firestoreError) {
                console.error("Error updating progress:", firestoreError);
                setError(`Failed to save progress: ${firestoreError.message}. Check Firebase rules.`);
                toast.error(`Failed to save progress: ${firestoreError.message}`); // Show toast error
            }
        }
    }, [currentUser, userId, allProblems]);

    // Main function to execute code - moved up
    const handleExecute = useCallback(async () => {
        // Clear previous output and results at the start of execution
        setOutput('');
        setDetailedTestResults([]);
        setLoading(true);
        setError(null);


        if (allProblems.length === 0 || !allProblems[currentProblemIndex]) {
            const err = "No problem selected or problems not loaded yet.";
            setError(err);
            // Removed toast.error(err); // This toast is now removed
            setLoading(false);
            return;
        }

        const problem = allProblems[currentProblemIndex];

        // Send the 'code' directly. The 'code' state now correctly holds only the method body,
        // and the backend is responsible for adding the 'public class Solution { ... }' wrapper.
        const codeToSend = code;

        // Deserialize test inputs and expected values before sending to backend
        const deserializedTests = problem.tests.map(test => {
            const deserializedInput = test.input.map(param => {
                // Check if this parameter is a serialized array
                if (param && typeof param === 'object' && param.__type === 'serialized_array') {
                    // Deserialize the nested array from JSON string
                    try {
                        return JSON.parse(param.value);
                    } catch (parseError) {
                        console.error('Error deserializing array parameter:', parseError);
                        return []; // Fallback to empty array on error
                    }
                }
                return param; // Keep primitives and non-serialized values as-is
            });
            
            // Also deserialize expected value if it's a serialized array
            let deserializedExpected = test.expected;
            if (test.expected && typeof test.expected === 'object' && test.expected.__type === 'serialized_array') {
                try {
                    deserializedExpected = JSON.parse(test.expected.value);
                } catch (parseError) {
                    console.error('Error deserializing expected array:', parseError);
                    deserializedExpected = []; // Fallback to empty array on error
                }
            }
            
            return {
                input: deserializedInput,
                expected: deserializedExpected
            };
        });

        const requestBody = {
            code: codeToSend, // Send the raw method body
            methodName: problem.methodName,
            testCases: deserializedTests,
        };

        try {
            // Use the configurable backend URL
            const response = await fetch(`${backendApiUrl}/execute`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                let errorDetails = `HTTP error! Status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorDetails += `. Details: ${errorData.errors || JSON.stringify(errorData)}`;
                } catch (e) {
                    errorDetails += `. Response text: ${await response.text()}`;
                }
                throw new Error(errorDetails);
            }

            const data = await response.json();

            let generalOutputMessages = '';
            let allTestsPassed = true;
            let passedTestsCount = 0;
            // Ensure totalTestsCount is available even if data.testResults is empty
            const totalTestsCount = problem.tests ? problem.tests.length : 0;

            if (data.errors) {
                generalOutputMessages += `Compilation/Execution Error:\n${data.errors}\n\n`;
                allTestsPassed = false;
            }

            if (data.output) {
                generalOutputMessages += `${data.output}\n`;
            }


            if (data.testResults && data.testResults.length > 0) {
                setDetailedTestResults(data.testResults);
                passedTestsCount = data.testResults.filter(testResult => testResult.passed).length;
                const backendAllTestsPassed = passedTestsCount === totalTestsCount;
                if (!backendAllTestsPassed) {
                    allTestsPassed = false;
                }
            } else {
                if (!data.errors) {
                    generalOutputMessages += "No detailed test results received from the backend. Ensure your backend is sending 'testResults'.\n";
                    if (!data.output) {
                        allTestsPassed = false;
                    }
                }
            }

            if (allTestsPassed && !data.errors) {
                generalOutputMessages += `Congratulations! You solved the problem.`;
                await updateProgress(problem.id, true, passedTestsCount, totalTestsCount, code); // Pass 'code' here
                // Removed toast.success('All tests passed!', { description: 'Congratulations! You solved the problem.' });
            } else {
                generalOutputMessages += `Issues detected. Please review the results.`;
                await updateProgress(problem.id, false, passedTestsCount, totalTestsCount, code); // Pass 'code' here
                // Removed toast.error('Code has issues.', { description: 'Please review the output and test results.' });
            }

            setOutput(generalOutputMessages.trim());

        } catch (execError) {
            setError(execError.message);
            setOutput(`Error during execution: ${execError.message}`);
            // Removed toast.error('Execution Error', { description: execError.message });
        } finally {
            setLoading(false);
        }
    }, [code, currentProblemIndex, updateProgress, allProblems, backendApiUrl]); // 'backendApiUrl' is now a dependency for handleExecute


    // Removed the previous useEffect for keyboard shortcut, now handled directly on Editor
    // No longer attached to codeEditorRef in this way

    // Function to fetch all problems from Firestore
    const fetchAllProblems = useCallback(async (knownDailyProblemId = null) => {
        setLoading(true); // Temporarily set loading for problem fetching
        setError(null);
        try {
            const problemsCollectionRef = collection(db, 'problems');
            const q = query(problemsCollectionRef);
            const querySnapshot = await getDocs(q);
            const loadedProblems = [];
            querySnapshot.forEach(doc => {
                loadedProblems.push({ id: doc.id, ...doc.data() }); // Include doc.id
            });
            // Sort problems by order field (hundreds-based system handles category grouping)
            // Then by title as a tiebreaker
            loadedProblems.sort((a, b) => {
                const orderA = a.order || 0;
                const orderB = b.order || 0;
                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                return a.title.localeCompare(b.title);
            });
            setAllProblems(loadedProblems);

            // If no daily problem is set yet, or if problems loaded after dailyProblem listener,
            // ensure current problem index is valid.
            // Set the current problem to the daily problem (or first problem if no daily problem set)
            if (loadedProblems.length > 0) {
                let targetIndex = 0; // Default to first problem
                const dailyId = knownDailyProblemId || dailyProblemId;

                if (dailyId) {
                    const dailyIndex = loadedProblems.findIndex(p => p.id === dailyId);
                    if (dailyIndex !== -1) {
                        targetIndex = dailyIndex;

                    } else {

                    }
                } else {

                }

                setCurrentProblemIndex(targetIndex);
                if (!dailyId) {
                    setDailyProblemId(loadedProblems[0]?.id || '');
                }
            }




        } catch (fetchError) {
            console.error("Error fetching problems:", fetchError);
            setError(`Failed to load problems: ${fetchError.message}`);
            toast.error(`Failed to load problems: ${fetchError.message}`); // Show toast error
        } finally {
            setLoading(false);
        }
    }, [dailyProblemId]); // Depend on dailyProblemId to re-evaluate after daily problem is set

    // Function to fetch sections managed by the current teacher
    const fetchSections = useCallback(async () => {
        if (!currentUser || !isTeacher) { // Only fetch if authenticated and is teacher
            setAllSections([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const sectionsCollectionRef = collection(db, 'sections');
            const q = query(sectionsCollectionRef, where('teacherId', '==', userId));
            const querySnapshot = await getDocs(q);
            const loadedSections = [];
            querySnapshot.forEach(doc => {
                const data = doc.data();
                loadedSections.push({
                    id: doc.id,
                    ...data,
                    studentIds: data.studentUids || [] // Map Firestore's 'studentUids' to 'studentIds'
                });
            });
            loadedSections.sort((a, b) => (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0));
            setAllSections(loadedSections);
        } catch (fetchError) {
            console.error("Error fetching sections:", fetchError);
            setError(`Failed to load sections: ${fetchError.message}`);
            toast.error(`Failed to load sections: ${fetchError.message}`); // Show toast error
        } finally {
            setLoading(false);
        }
    }, [currentUser, isTeacher, userId]);

    // Function to display the current problem based on currentProblemIndex
    // This function now ONLY updates problem details and code, not execution output/results
    const displayProblem = useCallback(() => {


        if (allProblems.length === 0 || currentProblemIndex < 0) {

            setCode('// Loading problems...');
            setMethodName('');
            return;
        }

        const problem = allProblems[currentProblemIndex];
        if (problem) {


            // Load saved code if available, otherwise use template code
            const problemProgress = studentSolutions[problem.id];
            const codeToSet = problemProgress?.savedCode || problem.templateCode || '';
            setCode(codeToSet);
            setMethodName(problem.methodName);
        } else {
            console.warn("Invalid currentProblemIndex or problem not found.");
            setCode('// Problem not found. Please select a valid problem.');
            setMethodName('');
        }
    }, [currentProblemIndex, allProblems, studentSolutions]); // Added studentSolutions as dependency

    // Main useEffect for initial data loading and Firestore listeners
    useEffect(() => {
        const unsubscribeFunctions = [];

        const setupListeners = async () => {
            if (currentUser && userId) {
                // --- Fetch user role and set isTeacher state ---
                try {
                    const userDocRef = doc(db, 'users', userId);
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                        const userData = userDocSnap.data();
                        setIsTeacher(userData.role === 'teacher');
                    } else {
                        console.warn("User profile not found in Firestore. Defaulting to student role.");
                        // Attempt to set default role for new users.
                        // This relies on security rules allowing write for new user to their own doc.
                        try {
                            await setDoc(userDocRef, { role: 'student', createdAt: Timestamp.now() }, { merge: true });
                            setIsTeacher(false);
                        } catch (setRoleError) {
                            console.error("Failed to set default role for new user:", setRoleError);
                            setError(`Failed to set default role for new user: ${setRoleError.message}`);
                            setIsTeacher(false); // Fallback to student role
                        }
                    }
                } catch (roleError) {
                    console.error("Error fetching user role:", roleError);
                    setError(`Failed to fetch user role: ${roleError.message}.`);
                    toast.error(`Failed to fetch user role: ${roleError.message}`); // Show toast error
                    setIsTeacher(false); // Default to student on error
                }


                // --- Load individual user progress ---
                const solvedProblemsColRef = collection(db, `users/${userId}/problems_solved`);
                const q = query(solvedProblemsColRef);
                try {
                    const querySnapshot = await getDocs(q);
                    const loadedSolutions = {};
                    querySnapshot.forEach((docSnap) => {
                        const data = docSnap.data();
                        loadedSolutions[docSnap.id] = {
                            completed: data.completed,
                            passedTests: data.passedTests || 0, // Default to 0 if not present
                            totalTests: data.totalTests || 0, // Default to 0 if not present
                            savedCode: data.savedCode || null // LOAD SAVED CODE
                        };
                    });
                    setStudentSolutions(loadedSolutions);
                } catch (firestoreError) {
                    console.error("Error loading individual progress:", firestoreError);
                    setError(`Failed to load personal progress: ${firestoreError.message}. Check Firebase rules.`);
                    toast.error(`Failed to load personal progress: ${firestoreError.message}`); // Show toast error
                }

                // --- Fetch daily problem ID and problems together ---
                const initializeProblems = async () => {
                    let dailyId = '';

                    try {
                        // First, fetch the daily problem ID
                        const dailyProblemDocRef = doc(db, "app_settings", "daily_problem");
                        const docSnap = await getDoc(dailyProblemDocRef);

                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            dailyId = data.currentProblemId;

                        } else {

                        }
                    } catch (error) {
                        console.error("Error fetching daily problem:", error);
                    }

                    // Set the daily problem ID
                    setDailyProblemId(dailyId);

                    // Then fetch all problems with the daily problem ID available
                    fetchAllProblems(dailyId);
                };

                initializeProblems();
                // Fetch sections if teacher. This now depends on the `isTeacher` state being updated.
                // We will move this into a separate useEffect or ensure `isTeacher` is set before this runs.

            } else {
                // Clear state for unauthenticated users
                localStorage.removeItem('studentSolutions');
                setStudentSolutions({});
                setAllProblems([]); // Clear problems if not authenticated
                setAllSections([]);
                setCurrentProblemIndex(0);
                setDailyProblemId('');
                setIsTeacher(false); // Ensure isTeacher is false for unauthenticated users
            }
        };

        setupListeners();

        return () => {
            unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
        };
    }, [currentUser, userId, fetchAllProblems, allProblems.length]);

    // useEffect for fetching sections, depends on `isTeacher` and `userId`
    useEffect(() => {
        // This useEffect will run when `isTeacher` state changes, ensuring `fetchSections` is called
        // only when the user's role has been determined and they are a teacher.
        if (currentUser && userId && isTeacher) {
            fetchSections();
        } else if (!isTeacher) { // Clear sections if user is no longer a teacher or logs out
            setAllSections([]);
        }
    }, [currentUser, userId, isTeacher, fetchSections]);

    // useEffect to fetch category display orders
    useEffect(() => {
        const fetchCategoryOrders = async () => {
            try {
                const categoriesColRef = collection(db, 'category_display_orders');
                const q = query(categoriesColRef, orderBy('order', 'asc'));
                const querySnapshot = await getDocs(q);
                const loadedOrders = [];
                querySnapshot.forEach((doc) => {
                    loadedOrders.push({ id: doc.id, ...doc.data() });
                });
                setCategoryDisplayOrders(loadedOrders);
            } catch (fetchError) {
                console.error("Error fetching category orders:", fetchError);
            }
        };

        fetchCategoryOrders();
    }, [allProblems]); // Re-fetch when problems change to reflect new categories

    // useEffect to call displayProblem whenever currentProblemIndex or allProblems change
    // This effect should NOT reset output/detailedTestResults
    useEffect(() => {
        // displayProblem();
        if (allProblems.length > 0 && currentProblemIndex >= 0 && currentProblemIndex < allProblems.length) {
            displayProblem();
        } else {
            console.warn("displayProblem skipped: allProblems not loaded or currentProblemIndex invalid.", {
                allProblemsLength: allProblems.length,
                currentProblemIndex
            });
        }
    }, [currentProblemIndex, displayProblem, allProblems]);


    const handleRestartProblem = useCallback(() => {
        if (allProblems.length === 0 || !allProblems[currentProblemIndex]) return;

        const problem = allProblems[currentProblemIndex];
        // Set code back to the original templateCode (without Solution class wrapper)
        setCode(problem.templateCode);
        setOutput(''); // Explicitly clear output
        setDetailedTestResults([]); // Explicitly clear test results
        setError(null);
        toast.info('Problem restarted.', { description: 'Code editor reset to template.' }); // Show toast
    }, [currentProblemIndex, allProblems]);

    const handleEditProblem = useCallback((problemId) => {
        if (allProblems.length === 0) return;
        
        // If problemId is provided, find that problem, otherwise use current problem
        const targetProblemId = problemId || allProblems[currentProblemIndex]?.id;
        const targetProblem = allProblems.find(p => p.id === targetProblemId);
        
        if (!targetProblem) return;

        // Set the problem to edit and switch to edit mode
        setProblemToEdit(targetProblem);
        setActiveView('edit');
        
        toast.info('Switching to edit mode', { description: `Editing: ${targetProblem.title}` });
    }, [allProblems, currentProblemIndex]);

    const handleSetCurrentProblemAsDaily = useCallback(async () => {
        setSettingProblemLoading(true);
        setError(null);
        try {
            if (allProblems.length === 0 || !allProblems[currentProblemIndex]) {
                throw new Error("No problem selected to set as daily problem.");
            }
            const dailyProblemDocRef = doc(db, "app_settings", "daily_problem");
            const problemIdToSet = allProblems[currentProblemIndex].id;

            await setDoc(dailyProblemDocRef, { currentProblemId: problemIdToSet }, { merge: true });
            toast.success('Daily problem set!', { description: `"${allProblems[currentProblemIndex].title}" is now the daily problem.` }); // Show toast
        } catch (setProblemError) {
            console.error("Error setting daily problem:", setProblemError);
            setError(`Failed to set daily problem: ${setProblemError.message}`);
            toast.error('Failed to set daily problem', { description: setProblemError.message }); // Show toast
        } finally {
            setSettingProblemLoading(false);
        }
        setIsTeacherMenuOpen(false);
    }, [currentProblemIndex, allProblems]);


    const handleNextDailyProblem = useCallback(async () => {
        setSettingProblemLoading(true);
        setError(null);
        try {
            const dailyProblemDocRef = doc(db, "app_settings", "daily_problem");
            const currentDailyProblemIdx = allProblems.findIndex(p => p.id === dailyProblemId);
            const nextIndex = currentDailyProblemIdx !== -1 ? currentDailyProblemIdx + 1 : 0;

            if (nextIndex < allProblems.length) {
                const nextProblem = allProblems[nextIndex];
                await setDoc(dailyProblemDocRef, { currentProblemId: nextProblem.id }, { merge: true });
                toast.success('Daily problem advanced!', { description: `Now on "${nextProblem.title}".` }); // Show toast
            } else {
                const errMsg = "No more problems available. All problems completed.";
                setError(errMsg);
                toast.info(errMsg); // Show toast
            }
        } catch (moveProblemError) {
            console.error("Error moving to next daily problem:", moveProblemError);
            setError(`Failed to move to next daily problem: ${moveProblemError.message}`);
            toast.error('Failed to advance daily problem', { description: moveProblemError.message }); // Show toast
        } finally {
            setLoading(false);
        }
        setIsTeacherMenuOpen(false);
    }, [dailyProblemId, allProblems]);


    const handleNextProblem = useCallback(() => {
        const nextProblemIndex = currentProblemIndex + 1;
        if (nextProblemIndex < allProblems.length) {
            setCurrentProblemIndex(nextProblemIndex);
            toast.info('Moving to next problem.', { description: `Now viewing: ${allProblems[nextProblemIndex].title}` }); // Show toast
        } else {
            setOutput(prevOutput => prevOutput + "\n\nYou have completed all the problems!");
            toast.success('All problems completed!', { description: 'You have completed all the problems for today!' }); // Show toast
        }
    }, [currentProblemIndex, allProblems]);


    const handleLogout = useCallback(async () => {
        try {
            await signOut(auth);
            toast.success('Logged out successfully.'); // Show toast
        } catch (error) {
            setError("Logout failed: " + error.message);
            toast.error('Logout failed', { description: error.message }); // Show toast
        }
    }, []);

    const handleViewProblemProgress = useCallback((problemId) => {
        if (allProblems.length === 0) return;
        
        // If problemId is provided, use it, otherwise use current problem
        const targetProblemId = problemId || allProblems[currentProblemIndex]?.id;
        const targetProblem = allProblems.find(p => p.id === targetProblemId);
        
        if (!targetProblem) return;

        // Switch to progress mode
        setActiveView('progress');
        
        toast.info('Viewing progress', { description: `Progress for: ${targetProblem.title}` });
    }, [allProblems, currentProblemIndex]);

    const currentProblemId = allProblems[currentProblemIndex]?.id;
    const currentProblemProgress = currentProblemId ? studentSolutions[currentProblemId] : undefined;
    const isProblemSolved = currentProblemProgress && currentProblemProgress.passedTests === currentProblemProgress.totalTests && currentProblemProgress.totalTests > 0;
    const hasNextProblemInList = (currentProblemIndex + 1) < allProblems.length;

    // Updated toggleManager to control activeView
    const toggleManager = useCallback((managerName) => {
        setActiveView(prevView => {
            if (prevView === managerName) {
                // If clicking the active manager, switch back to solve
                setProblemToEdit(null); // Clear any problem being edited
                return 'solve';
            } else {
                // Otherwise, switch to the clicked manager
                return managerName;
            }
        });
        setIsTeacherMenuOpen(false); // Close teacher menu when a manager is selected
    }, []);


    // NEW: Function to group problems by category
    const groupedProblems = useMemo(() => {
        const groups = {};
        allProblems.forEach(problem => {
            const category = problem.category || 'Uncategorized';
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(problem);
        });

        // Convert groups object to an array for sorting
        let groupedArray = Object.keys(groups).map(categoryName => ({
            categoryName,
            problems: groups[categoryName].sort((a, b) => {
                // Sort problems within each category by their order field
                const orderA = a.order || 0;
                const orderB = b.order || 0;
                return orderA - orderB;
            })
        }));

        // Sort categories based on the fetched categoryDisplayOrders
        groupedArray.sort((a, b) => {
            const orderA = categoryDisplayOrders.find(c => c.id === a.categoryName)?.order;
            const orderB = categoryDisplayOrders.find(c => c.id === b.categoryName)?.order;

            // Handle cases where a category might not yet have an explicit order (put them at the end)
            if (orderA === undefined && orderB === undefined) {
                return a.categoryName.localeCompare(b.categoryName); // Alphabetical for uncategorized
            }
            if (orderA === undefined) return 1; // a goes after b
            if (orderB === undefined) return -1; // a goes before b

            return orderA - orderB; // Sort by order value
        });

        return groupedArray;
    }, [allProblems, categoryDisplayOrders]);

    // NEW: Toggle category expansion
    const toggleCategoryExpand = useCallback((categoryName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    }, []);

    // Keyboard shortcuts handler
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Global shortcuts
            if (e.key === 'Escape') {
                // Return to solve view from any other view
                if (activeView !== 'solve') {
                    setActiveView('solve');
                    setProblemToEdit(null);
                    toast.info('Returned to solve view');
                }
                return;
            }
            
            // Only handle other shortcuts when in solve view and user is teacher
            if (activeView === 'solve' && isTeacher && (e.ctrlKey || e.metaKey)) {
                switch (e.key) {
                    case 'e':
                        e.preventDefault();
                        handleEditProblem();
                        break;
                    case 'p':
                        e.preventDefault();
                        handleViewProblemProgress();
                        break;
                    default:
                        break;
                }
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [activeView, isTeacher, handleEditProblem, handleViewProblemProgress]);


    return (
        <div className="min-h-screen bg-mad-timberwolf text-gray-800 p-4 flex"> {/* Changed text color to gray-800 */}
            {/* Sidebar */}
            <div className={cn(
                "fixed inset-y-0 left-0 z-40 w-64 bg-mad-gunmetal text-white transition-transform duration-300 ease-in-out flex flex-col", /* Changed to mad-gunmetal */
                isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="flex justify-between items-center p-4 pb-6 flex-shrink-0">
                    <h2 className="text-xl font-bold text-mad-fulvous">Problems</h2> {/* Changed to mad-fulvous */}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsSidebarOpen(false)}
                        className="text-mad-peel hover:text-white" /* Changed to mad-peel */
                    >
                        <X className="h-6 w-6" />
                    </Button>
                </div>
                <div className="flex flex-col flex-1 px-4 pb-4 min-h-0">
                    <div className="flex-1 overflow-y-auto min-h-0">
                        <ul className="space-y-2">
                        {allProblems.length === 0 ? (
                            <li className="text-gray-400 italic">No problems available.</li>
                        ) : (
                            groupedProblems.map(group => (
                                <li key={group.categoryName} className="mb-2">
                                    <Button
                                        variant="ghost"
                                        onClick={() => toggleCategoryExpand(group.categoryName)}
                                        className="w-full justify-between text-left px-3 py-2 rounded-md font-bold hover:bg-mad-fulvous/50 transition-colors duration-200 text-white" /* Changed to mad-dart-green and mad-fulvous */
                                    >
                                        <span>{group.categoryName}</span>
                                        {expandedCategories[group.categoryName] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </Button>
                                    {expandedCategories[group.categoryName] && (
                                        <ul className="pl-4 mt-2 space-y-1">
                                            {group.problems.map((problem) => {
                                                // Find the actual index in the flat allProblems array for problem selection
                                                const flatIndex = allProblems.findIndex(p => p.id === problem.id);
                                                // Use composite ordering: categoryOrder * 1000 + problemOrder
                                                const dailyProblem = allProblems.find(p => p.id === dailyProblemId);
                                                const dailyProblemCategoryOrder = categoryDisplayOrders.find(c => c.id === (dailyProblem?.category || 'Uncategorized'))?.order || 0;
                                                const dailyProblemCompositeOrder = (dailyProblemCategoryOrder * 1000) + (dailyProblem?.order || 0);

                                                const problemCategoryOrder = categoryDisplayOrders.find(c => c.id === (problem.category || 'Uncategorized'))?.order || 0;
                                                const problemCompositeOrder = (problemCategoryOrder * 1000) + (problem.order || 0);

                                                const isAccessibleForStudent = problemCompositeOrder <= dailyProblemCompositeOrder;
                                                const isDisabled = !isTeacher && !isAccessibleForStudent;



                                                const problemProgress = studentSolutions[problem.id];
                                                const displayProgress = problemProgress ?
                                                    `${problemProgress.passedTests}/${problemProgress.totalTests}` : '0/' + `${problem.testCases?.length || 0}`; // Show 0/total if no progress
                                                const progressColorClass = problemProgress?.passedTests === problemProgress?.totalTests && problemProgress?.totalTests > 0
                                                    ? 'text-green-400'
                                                    : (problemProgress?.passedTests > 0 ? 'text-mad-fulvous' : 'text-red-400'); // Changed to mad-fulvous for partial

                                                return (
                                                    <li key={problem.id}>
                                                        <Button
                                                            variant="ghost"
                                                            className={cn(
                                                                "w-full justify-start text-left px-3 py-2 rounded-md",
                                                                "hover:bg-mad-fulvous/20 transition-colors duration-200", /* Lighter fulvous hover */
                                                                currentProblemIndex === flatIndex && "bg-mad-fulvous/80 hover:bg-mad-fulvous", /* Darker fulvous for selected */
                                                                isDisabled && "opacity-50 cursor-not-allowed text-gray-500 hover:bg-gray-800"
                                                            )}
                                                            onClick={() => !isDisabled && setCurrentProblemIndex(flatIndex)}
                                                            disabled={isDisabled}
                                                        >
                                                            <div className="flex items-center justify-between w-full">
                                                                <span className="flex items-center">
                                                                    {problem.title}
                                                                    {getDailyProblemIndicator(problem.id)}
                                                                </span>
                                                                <span className={cn("text-xs font-semibold ml-2", progressColorClass)}>
                                                                    {displayProgress}
                                                                </span>
                                                            </div>
                                                        </Button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            ))
                        )}
                        </ul>
                    </div>

                    {/* Sidebar Footer - Teacher Management Actions */}
                    {isTeacher && (
                        <div className="border-t border-mad-border-color pt-4 mt-4 space-y-2 flex-shrink-0">
                            <Button
                                onClick={() => setActiveView('manage')}
                                variant="ghost"
                                className={cn(
                                    "w-full justify-start text-left px-3 py-2 rounded-md",
                                    "text-mad-timberwolf hover:bg-mad-fulvous/20 hover:text-white",
                                    "transition-colors duration-200 flex items-center gap-2",
                                    activeView === 'manage' && "bg-mad-fulvous/80 text-white"
                                )}
                            >
                                <Settings className="w-4 h-4" />
                                <span className="text-sm">Manage All Problems</span>
                            </Button>
                            <Button
                                onClick={() => setActiveView('progress')}
                                variant="ghost"
                                className={cn(
                                    "w-full justify-start text-left px-3 py-2 rounded-md",
                                    "text-mad-timberwolf hover:bg-mad-peel/20 hover:text-white",
                                    "transition-colors duration-200 flex items-center gap-2",
                                    activeView === 'progress' && "bg-mad-peel/80 text-white"
                                )}
                            >
                                <BarChart2 className="w-4 h-4" />
                                <span className="text-sm">Class Progress</span>
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content Area */}
            <div className={cn(
                "flex-1 p-4 md:p-8 transition-all duration-300 ease-in-out",
                isSidebarOpen ? "ml-64" : "ml-0"
            )}>
                {/* Fixed Header Bar with Toggle Buttons */}
                <div className={cn(
                    "fixed top-0 right-0 z-30 flex items-center p-4 bg-mad-gunmetal/90 backdrop-blur-md shadow-lg border-b border-mad-dart-green transition-all duration-300 ease-in-out", /* Changed to mad-gunmetal and mad-dart-green */
                    isSidebarOpen ? "left-64" : "left-0"
                )}>
                    {/* Toggle Sidebar Button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsSidebarOpen(true)}
                        className={cn(
                            "text-white hover:bg-gray-700 hover:text-white rounded-full p-2 mr-2",
                            isSidebarOpen ? "opacity-0 pointer-events-none" : "opacity-100"
                        )}
                    >
                        <Menu className="h-6 w-6" />
                    </Button>

                    <div className="flex items-center justify-start flex-grow"> {/* Centering the logo and title */}
                        <img src={process.env.PUBLIC_URL + "/mad_owl_logo.png"} alt="MAD Owl Logo" className="h-16 w-auto mr-3 sm:mr-4" /> {/* Increased height to h-16 */}
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-mad-fulvous to-mad-peel"> {/* Changed to mad-fulvous and mad-peel gradient */}
                            Method a Day (MAD)
                        </h1>
                    </div>

                    <div className="flex items-center gap-2 ml-auto">
                        {/* Teacher Controls Menu */}
                        {isTeacher && (
                            <div ref={teacherMenuRef} className="relative flex-shrink-0">
                                <Button
                                    onClick={() => setIsTeacherMenuOpen(!isTeacherMenuOpen)}
                                    variant="outline"
                                    className="bg-mad-dart-green text-mad-fulvous border-mad-fulvous/50 hover:bg-mad-fulvous/20 hover:text-mad-fulvous flex items-center gap-2 rounded-full shadow-lg px-2 py-1 sm:px-4 sm:py-2 text-sm md:text-base" /* Changed to mad-dart-green and mad-fulvous */
                                >
                                    <Settings className="w-5 h-5" />
                                    <span className="hidden sm:inline">Teacher Actions</span> <ChevronDown className={cn("w-4 h-4 transition-transform", isTeacherMenuOpen ? "rotate-180" : "rotate-0")} />
                                </Button>
                                {isTeacherMenuOpen && (
                                    <Card className="absolute right-0 mt-2 p-4 bg-mad-dart-green border border-mad-fulvous/50 rounded-lg shadow-xl z-50 w-64 space-y-3"> /* Changed to mad-dart-green and mad-fulvous */
                                        <CardTitle className="text-sm font-semibold text-mad-peel mb-2">Daily Problem Control</CardTitle> {/* Changed to mad-peel */}
                                        <Button
                                            onClick={handleSetCurrentProblemAsDaily}
                                            disabled={settingProblemLoading || allProblems.length === 0}
                                            className={cn(
                                                "w-full justify-start bg-mad-fulvous text-white px-3 py-2 rounded-md shadow-md", /* Changed to mad-fulvous */
                                                "hover:bg-mad-fulvous/80 transition-colors duration-200",
                                                "disabled:opacity-50 disabled:cursor-not-allowed",
                                                "flex items-center gap-1 text-sm"
                                            )}
                                        >
                                            {settingProblemLoading ? (
                                                <>
                                                    <Loader2 className="animate-spin w-4 h-4" /> Setting...
                                                </>
                                            ) : (
                                                <>
                                                    Set as Daily
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            onClick={handleNextDailyProblem}
                                            disabled={settingProblemLoading || !hasNextProblemInList}
                                            className={cn(
                                                "w-full justify-start bg-mad-dart-green text-white px-3 py-2 rounded-md shadow-md", /* Changed to mad-dart-green */
                                                "hover:bg-mad-dart-green/80 transition-colors duration-200",
                                                "disabled:opacity-50 disabled:cursor-not-allowed",
                                                "flex items-center gap-1 text-sm"
                                            )}
                                        >
                                            {settingProblemLoading ? (
                                                <>
                                                    <Loader2 className="animate-spin w-4 h-4" /> Moving...
                                                </>
                                            ) : (
                                                <>
                                                    Next Daily Problem <Plus className="w-4 h-4" />
                                                </>
                                            )}
                                        </Button>
                                        <hr className="border-mad-fulvous/30 my-2" /> {/* Changed to mad-fulvous */}
                                        <CardTitle className="text-sm font-semibold text-mad-peel mb-2">Managers</CardTitle> {/* Changed to mad-peel */}
                                        <Button
                                            onClick={() => toggleManager('manage')}
                                            variant="outline"
                                            className="w-full justify-start text-mad-fulvous border-mad-fulvous hover:bg-mad-fulvous/20 hover:text-white rounded-full" /* Changed to mad-fulvous */
                                        >
                                            <Puzzle className="w-4 h-4 mr-1" />
                                            {activeView === 'manage' ? 'Hide Problem Manager' : 'Show Problem Manager'}
                                        </Button>
                                        <Button
                                            onClick={() => toggleManager('sections')}
                                            variant="outline"
                                            className="w-full justify-start text-mad-peel border-mad-peel hover:bg-mad-peel/20 hover:text-mad-peel flex items-center gap-1 text-sm rounded-full" /* Changed to mad-peel */
                                        >
                                            <BookOpen className="w-4 h-4 mr-1" />
                                            {activeView === 'sections' ? 'Hide Section Manager' : 'Show Section Manager'}
                                        </Button>
                                        <Button
                                            onClick={() => toggleManager('progressManager')}
                                            variant="outline"
                                            className="w-full justify-start text-mad-peel border-mad-peel hover:bg-mad-peel/20 hover:text-mad-peel flex items-center gap-1 text-sm rounded-full" /* Changed to mad-peel */
                                        >
                                            <BarChart2 className="w-4 h-4 mr-1" />
                                            {activeView === 'progressManager' ? 'Hide Progress Manager' : 'Show Progress Manager'}
                                        </Button>
                                        <Button
                                            onClick={() => toggleManager('progress')}
                                            variant="outline"
                                            className="w-full justify-start text-mad-peel border-mad-peel hover:bg-mad-peel/20 hover:text-mad-peel flex items-center gap-1 text-sm rounded-full" /* Changed to mad-peel */
                                        >
                                            <LayoutDashboard className="w-4 h-4 mr-1" />
                                            Progress Dashboard
                                        </Button>
                                    </Card>
                                )}
                            </div>
                        )}
                        <div className="text-xs text-gray-300 text-right hidden sm:block flex-shrink-0">
                            <span className="truncate block">
                                <span className="font-semibold">{currentUser?.email || 'N/A'}</span>
                            </span>
                            <span className="font-medium block">
                                <span className="font-semibold">{isTeacher ? 'Teacher' : 'Student'}</span>
                            </span>
                        </div>
                        <Button
                            onClick={handleLogout}
                            variant="ghost"
                            className="text-red-400 hover:bg-red-500/20 hover:text-red-300 flex items-center gap-1 flex-shrink-0 rounded-full"
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="hidden sm:inline">Logout</span>
                        </Button>
                    </div>
                </div>


                <div className="max-w-7xl mx-auto space-y-6 pt-20">
                    {/* Mode Switcher Tabs - Only show for teachers */}
                    {isTeacher && (
                        <div className="flex items-center justify-center mb-6">
                            <div className="bg-mad-gunmetal/20 backdrop-blur-md border border-mad-gunmetal/30 rounded-lg p-1 flex gap-1">
                                <button
                                    onClick={() => setActiveView('solve')}
                                    className={cn(
                                        "px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2",
                                        activeView === 'solve'
                                            ? "bg-mad-fulvous text-white shadow-md"
                                            : "text-mad-gunmetal hover:bg-mad-fulvous/10 hover:text-mad-fulvous"
                                    )}
                                >
                                    <Play className="w-4 h-4" />
                                    Solve
                                </button>
                                <button
                                    onClick={() => handleEditProblem()}
                                    className={cn(
                                        "px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2",
                                        activeView === 'edit'
                                            ? "bg-mad-peel text-mad-gunmetal shadow-md"
                                            : "text-mad-gunmetal hover:bg-mad-peel/10 hover:text-mad-peel"
                                    )}
                                >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleViewProblemProgress()}
                                    className={cn(
                                        "px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2",
                                        activeView === 'progress'
                                            ? "bg-mad-dart-green text-white shadow-md"
                                            : "text-mad-gunmetal hover:bg-mad-dart-green/10 hover:text-mad-dart-green"
                                    )}
                                >
                                    <BarChart2 className="w-4 h-4" />
                                    Progress
                                </button>
                            </div>
                        </div>
                    )}


                    {/* Main Coding Interface - Only show if no manager/dashboard is active */}
                    {activeView === 'solve' && (
                        <>
                            {/* COMBINED Problem Description and Code Editor Card */}
                            <Card className="bg-mad-timberwolf backdrop-blur-md border border-mad-gunmetal/50 shadow-lg rounded-xl"> {/* Theme color */}
                                <CardHeader className="pb-4">
                                    <div className="flex items-start justify-between">
                                        <CardTitle className="text-lg sm:text-xl text-mad-fulvous flex items-center gap-2 mb-2"> {/* Theme color */}
                                            <Puzzle className="w-5 h-5 text-mad-fulvous" /> {/* Theme color */}
                                            Current Problem: {allProblems[currentProblemIndex]?.title || 'Loading Problem...'}
                                            {allProblems[currentProblemIndex] && getDailyProblemIndicator(allProblems[currentProblemIndex].id)}
                                            <ProblemHeaderActions
                                                problem={allProblems[currentProblemIndex]}
                                                isTeacher={isTeacher}
                                                onEdit={handleEditProblem}
                                                onProgress={handleViewProblemProgress}
                                            />
                                        </CardTitle>
                                        <div className="flex gap-2 ml-4">
                                            <Button
                                                onClick={handleRestartProblem}
                                                size="sm"
                                                variant="outline"
                                                className="text-mad-dart-green border-mad-dart-green hover:bg-mad-dart-green/20 hover:text-white rounded-full px-3 py-1 text-xs"
                                            >
                                                <RotateCcw className="w-3 h-3 mr-1" />
                                                Restart
                                            </Button>
                                        </div>
                                    </div>
                                    <CardDescription className="text-gray-800 text-base font-medium prose prose-sm max-w-none"> {/* Changed to gray-800, added prose classes */}
                                        <ReactMarkdown>
                                            {allProblems[currentProblemIndex]?.description || 'Problem description loading...'}
                                        </ReactMarkdown>
                                        <br />
                                        <strong>• Example:</strong> <span className="font-normal">{allProblems[currentProblemIndex]?.example || 'Example loading...'}</span>
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="pt-0 space-y-4 relative">
                                    <div className="relative">
                                        <Editor
                                            value={code}
                                            onValueChange={setCode}
                                            highlight={code => highlight(code, languages.java, 'java')}
                                            padding={10}
                                            className="min-h-[300px] bg-black/80 text-white border-mad-gunmetal/50 rounded-lg shadow-inner
                                                    focus:outline-none focus:ring-2 focus:ring-mad-fulvous focus:border-transparent
                                                    font-mono text-sm overflow-auto" /* Theme color */
                                            style={{
                                                fontFamily: '"Fira code", "Fira Mono", monospace',
                                                fontSize: 14,
                                            }}
                                            ref={codeEditorRef}
                                            onKeyDown={(e) => { // Added onKeyDown handler directly to Editor
                                                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                                                    e.preventDefault(); // Prevent newline
                                                    if (!loading) { // Only run if not already loading
                                                        handleExecute();
                                                    }
                                                }
                                            }}
                                        />

                                        {/* Floating String Methods Button - Only show for String category problems */}
                                        {allProblems[currentProblemIndex]?.category === 'Strings' && (
                                            <Button
                                                onClick={() => setIsStringMethodsOpen(true)}
                                                className={cn(
                                                    "absolute bottom-4 left-4 z-10",
                                                    "bg-mad-dart-green text-white px-3 py-2 rounded-full shadow-lg",
                                                    "hover:bg-mad-dart-green/80 hover:scale-105 transition-all duration-300",
                                                    "flex items-center gap-2"
                                                )}
                                            >
                                                <BookOpen className="w-4 h-4" />
                                                <span className="text-xs font-medium">String Methods</span>
                                            </Button>
                                        )}

                                        {/* Floating Run Code Button */}
                                        <Button
                                            onClick={handleExecute}
                                            disabled={loading}
                                            className={cn(
                                                "absolute bottom-4 right-4 z-10",
                                                "bg-mad-fulvous text-white px-4 py-2 rounded-full shadow-lg", /* Theme color */
                                                "hover:bg-mad-fulvous/80 hover:scale-105 transition-all duration-300",
                                                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                                                "flex items-center gap-2 text-sm font-medium"
                                            )}
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="animate-spin w-4 h-4" />
                                                    Running...
                                                </>
                                            ) : (
                                                <>
                                                    <Play className="w-4 h-4" />
                                                    Run Code
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Results Card - now below code card */}
                            <Card className="bg-mad-timberwolf backdrop-blur-md border border-mad-gunmetal/50 shadow-lg flex-1 rounded-xl"> {/* Theme color */}
                                <CardHeader>
                                    <CardTitle className="text-lg sm:text-xl text-mad-fulvous flex items-center gap-2"> {/* Theme color */}
                                        <Terminal className="w-5 h-5 text-mad-fulvous" /> {/* Theme color */}
                                        Output & Test Results
                                    </CardTitle>
                                    <CardDescription className="text-gray-600">Review compilation output and individual test case results.</CardDescription> {/* Changed to gray-600 */}
                                </CardHeader>
                                <CardContent>
                                    {output && (
                                        <div className="mb-4">
                                            <h3 className="text-md font-semibold text-gray-800 mb-2">General Output:</h3> {/* Changed to gray-800 */}
                                            <Textarea
                                                value={output}
                                                readOnly
                                                className="min-h-[100px] bg-black/80 text-mad-peel border-mad-gunmetal/50 rounded-lg shadow-inner
                                                        font-mono text-sm whitespace-pre-wrap" /* Theme color */
                                                placeholder="Compilation messages and console output will appear here."
                                            />
                                        </div>
                                    )}

                                    {/* Re-added the detailedTestResults table */}
                                    {detailedTestResults.length > 0 && (
                                        <div>
                                            <h3 className="text-md font-semibold text-gray-800 mb-2">Test Case Results:</h3> {/* Changed to gray-800 */}
                                            <div className="overflow-x-auto rounded-lg border border-mad-gunmetal/50"> {/* Theme color */}
                                                <table className="w-full text-left text-sm text-gray-700"> {/* Changed to gray-700 */}
                                                    <thead className="text-xs uppercase bg-gray-700 text-gray-300">
                                                        <tr>
                                                            <th scope="col" className="px-4 py-2 whitespace-nowrap">Test Input</th>
                                                            <th scope="col" className="px-4 py-2 whitespace-nowrap">Actual Output</th>
                                                            <th scope="col" className="px-4 py-2 whitespace-nowrap">Expected Output</th>
                                                            <th scope="col" className="px-4 py-2 text-center whitespace-nowrap">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detailedTestResults.map((testResult, index) => (
                                                            <tr key={index} className="text-gray-300 border-b bg-black/80 border-mad-gunmetal/50 hover:bg-gray-700/50"> {/* Theme color */}
                                                                <td className="px-4 py-2 font-mono whitespace-nowrap">
                                                                    {/* Stringify complex inputs, handle null/undefined */}
                                                                    {testResult.input !== undefined && testResult.input !== null ? JSON.stringify(testResult.input) : 'null'}
                                                                </td>
                                                                <td className=" px-4 py-2 font-mono whitespace-nowrap">
                                                                    {/* Stringify complex outputs, handle null/undefined */}
                                                                    {testResult.actual !== undefined && testResult.actual !== null ? JSON.stringify(testResult.actual) : 'null'}
                                                                </td>
                                                                <td className="px-4 py-2 font-mono whitespace-nowrap">
                                                                    {testResult.expected !== undefined && testResult.expected !== null ? JSON.stringify(testResult.expected) : 'null'}
                                                                </td>
                                                                <td className="px-4 py-2 text-center whitespace-nowrap">
                                                                    {testResult.passed ? (
                                                                        <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                                                                    ) : (
                                                                        <XCircle className="w-5 h-5 text-red-400 mx-auto" />
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                    {output === '' && detailedTestResults.length === 0 && !loading && (
                                        <p className="text-gray-600 italic">Run your code to see output and test results here.</p>
                                    )}
                                </CardContent>
                            </Card>

                            {error && (
                                <Alert variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/50 rounded-lg">
                                    <XCircle className="h-4 w-4" />
                                    <AlertTitle>Error</AlertTitle>
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}
                        </>
                    )}

                            {/* Edit Mode Content */}
                            {activeView === 'edit' && isTeacher && (
                                <ProblemManager
                                    onProblemAddedOrUpdated={fetchAllProblems}
                                    initialProblems={allProblems}
                                    problemToEdit={allProblems[currentProblemIndex]}
                                    onEditComplete={() => {}}
                                    isTeacher={isTeacher}
                                    onClose={() => setActiveView('solve')}
                                />
                            )}

                            {/* Progress Mode Content */}
                            {activeView === 'progress' && isTeacher && (
                                <ProgressDashboard
                                    allProblems={allProblems}
                                    isTeacher={isTeacher}
                                    dailyProblemId={dailyProblemId}
                                    onClose={() => setActiveView('solve')}
                                />
                            )}

                            {/* Sections Mode Content */}
                            {activeView === 'sections' && isTeacher && (
                                <SectionManager 
                                    onSectionAddedOrUpdated={fetchSections} 
                                    onClose={() => setActiveView('solve')}
                                />
                            )}

                    {/* Section Manager Component (Teacher Only) */}
                    {activeView === 'sections' && isTeacher && (
                        <SectionManager 
                            onSectionAddedOrUpdated={fetchSections} 
                            onClose={() => setActiveView('solve')}
                        />
                    )}

                    {/* Progress Manager Component (Teacher Only) */}
                    {activeView === 'progressManager' && isTeacher && (
                        <ProgressManager 
                            allProblems={allProblems} 
                            isTeacher={isTeacher} 
                            teacherSections={allSections}
                            onClose={() => setActiveView('solve')}
                        />
                    )}

                    {/* Progress Dashboard Component (Teacher Only) */}
                    {activeView === 'progress' && isTeacher && (
                        <ProgressDashboard
                            allProblems={allProblems}
                            isTeacher={isTeacher}
                            dailyProblemId={dailyProblemId}
                            onClose={() => setActiveView('solve')}
                        />
                    )}
                </div>
            </div>

            {/* String Methods Reference Modal */}
            <StringMethodsReference
                isOpen={isStringMethodsOpen}
                onClose={() => setIsStringMethodsOpen(false)}
            />
        </div>
    );
};

export default CodeExecutorApp;
